import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dialog, DialogContent, DialogContentText } from "@mui/material";
import { useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import useAuthorization from "src/custom-hooks/useAuthorization/useAuthorization";
import { DocumentTemplatesApiService } from "src/services/document-templates/document-templates-api.service";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import { toastifyResponse } from "src/utils/toastify-response/toastify-response";
import { DeleteButton } from "./styles";
import { DocumentTemplateDTO, DocumentTemplateSTO } from "../../../models/document-templates";
import DialogActions from "../../DialogActions/DialogActions";
import DialogTitle from "../../DialogTitle/DialogTitle";

interface RemoveDocumentTemplateProps {
  item: DocumentTemplateDTO;
  handleNotifyParent: () => void;
}

interface State {
  isDialogOpen: boolean;
  isLoading: boolean;
}

const RemoveDocumentTemplate = ({ item, handleNotifyParent }: RemoveDocumentTemplateProps) => {
  const { t } = useTranslation();
  const hasAdminAccess = useAuthorization([AUTHORIZATIONS.CompanyAccountsAccess.LegalEntityAdminAccess]);

  const [state, setState] = useState<State>({
    isDialogOpen: false,
    isLoading: false,
  });

  const handleDeleteItem = React.useCallback(async () => {
    try {
      const deletedTemplate = {
        ...item,
        isDeleted: true,
      };

      const response = await new DocumentTemplatesApiService().saveItems([
        deletedTemplate as unknown as DocumentTemplateSTO,
      ]);

      if (!response.error) {
        handleNotifyParent();
      } else {
        toastifyResponse(response, "failed");
      }
    } catch (error) {
      console.error(error);
    }
  }, [handleNotifyParent, item]);

  return (
    <>
      <DeleteButton
        className="template-delete-button"
        title={`${t(`components.confirmation_remove_item_dialog.title`)}`}
        color="error"
        onClick={(e) => {
          e.stopPropagation();
          setState((prevState) => ({ ...prevState, isDialogOpen: true }));
        }}
        disabled={!hasAdminAccess}
      >
        <FontAwesomeIcon icon={faTrash} />
      </DeleteButton>

      <Dialog
        open={state.isDialogOpen}
        onClose={() => setState((prevState) => ({ ...prevState, isDialogOpen: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          title={t(`components.confirmation_remove_item_dialog.title`)}
          handleClose={() => setState((prevState) => ({ ...prevState, isDialogOpen: false }))}
        />
        <DialogContent>
          <DialogContentText>{t(`components.confirmation_remove_item_dialog.message`)}</DialogContentText>
        </DialogContent>
        <DialogActions
          handleCancel={() => setState((prevState) => ({ ...prevState, isDialogOpen: false }))}
          handleDelete={handleDeleteItem}
          customDeleteName={state.isLoading ? t("common_words.deleting") : t("common_words.delete")}
          canBeDeleted={!state.isLoading}
        />
      </Dialog>
    </>
  );
};

export default RemoveDocumentTemplate;
